import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import { US_STATES, getCitiesForState, getZipsForCity } from '../../data/usLocations';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired } from '../../utils/formUtils';

interface PersonalInformationProps {
  data?: any; // For backward compatibility
  onChange?: (data: any) => void; // For backward compatibility
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  first_name: string;
  middle_name: string;
  last_name: string;
  street_address: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  email: string;
  physical_address_different: string;
  telephone: string;
  mobile: string;
  other_phone: string;
  emergency_contact_name: string;
  emergency_contact_relationship: string;
  emergency_contact_phone: string;
  ssn_last_four: string;
  birthplace: string;
  date_of_birth: string;
  age: string;
  marital_status: string;
  number_of_children: string;
  children_ages: string;
  drivers_license: boolean;
  state_id: boolean;
  cdl_endorsements: boolean;
  cdl_endorsements_type: string;
}

const PersonalInformation: React.FC<PersonalInformationProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  // Use new props if available, otherwise fall back to old props
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => {
      /* noop */
    });

  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'personal_info', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'personal_info', fieldMappings);

  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    middle_name: '',
    last_name: '',
    street_address: '',
    city: '',
    state: '',
    zip: '',
    county: '',
    email: '',
    physical_address_different: '',
    telephone: '',
    mobile: '',
    other_phone: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    ssn_last_four: '',
    birthplace: '',
    date_of_birth: '',
    age: '',
    marital_status: '',
    number_of_children: '',
    children_ages: '',
    drivers_license: false,
    state_id: false,
    cdl_endorsements: false,
    cdl_endorsements_type: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return;

    let updatedData = { ...formData, [field]: value };

    if (field === 'state') {
      updatedData = { ...updatedData, city: '', zip: '' };
    } else if (field === 'city') {
      updatedData = { ...updatedData, zip: '' };
    }

    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    handleChange(e.target.name, e.target.value);
  };

  const availableCities = getCitiesForState(formData.state);
  const availableZips = getZipsForCity(formData.state, formData.city);

  // Helper to get common props for form fields

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Personal Information
        {readOnly && (
          <Typography
            component='span'
            variant='body2'
            color='warning.main'
            sx={{ ml: 2, fontStyle: 'italic', fontWeight: 'bold' }}
          >
            (Read Only - View Only)
          </Typography>
        )}
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2,
        opacity: readOnly ? 0.8 : 1
      }}>
        {/* Name */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!isFieldHiddenLocal('first_name') && (
            <TextField
              label='First Name'
              value={formData.first_name}
              onChange={e => handleChange('first_name', e.target.value)}
              required={isFieldRequiredLocal('first_name')}
              sx={{ flex: 1, minWidth: 200 }}
              disabled={readOnly}
            />
          )}
          {!isFieldHiddenLocal('middle_name') && (
            <TextField
              label='Middle Name'
              value={formData.middle_name}
              onChange={e => handleChange('middle_name', e.target.value)}
              required={isFieldRequiredLocal('middle_name')}
              sx={{ flex: 1, minWidth: 200 }}
              disabled={readOnly}
            />
          )}
          {!isFieldHiddenLocal('last_name') && (
            <TextField
              label='Last Name'
              value={formData.last_name}
              onChange={e => handleChange('last_name', e.target.value)}
              required={isFieldRequiredLocal('last_name')}
              sx={{ flex: 1, minWidth: 200 }}
              disabled={readOnly}
            />
          )}
        </Box>

        {/* Address */}
        {!isFieldHiddenLocal('street_address') && (
          <TextField
            fullWidth
            label='Street Address'
            multiline
            rows={2}
            value={formData.street_address}
            onChange={e => handleChange('street_address', e.target.value)}
            required={isFieldRequiredLocal('street_address')}
            disabled={readOnly}
          />
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!isFieldHiddenLocal('state') && (
            <FormControl sx={{ flex: 1, minWidth: 150 }} required={isFieldRequiredLocal('state')}>
              <InputLabel>State</InputLabel>
              <Select
                name='state'
                value={formData.state}
                label='State'
                onChange={handleSelectChange}
                disabled={readOnly}
              >
                {US_STATES.map(state => (
                  <MenuItem key={state.abbreviation} value={state.abbreviation}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!isFieldHiddenLocal('city') && (
            <FormControl
              sx={{ flex: 1, minWidth: 150 }}
              required={isFieldRequiredLocal('city')}
              disabled={readOnly || !formData.state}
            >
              <InputLabel>City</InputLabel>
              <Select
                name='city'
                value={formData.city}
                label='City'
                onChange={handleSelectChange}
              >
                {availableCities.map(city => (
                  <MenuItem key={city.name} value={city.name}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!isFieldHiddenLocal('zip') && (
            <FormControl
              sx={{ flex: 1, minWidth: 150 }}
              required={isFieldRequiredLocal('zip')}
              disabled={readOnly || !formData.city}
            >
              <InputLabel>ZIP Code</InputLabel>
              <Select
                name='zip'
                value={formData.zip}
                label='ZIP Code'
                onChange={handleSelectChange}
              >
                {availableZips.map(zip => (
                  <MenuItem key={zip} value={zip}>
                    {zip}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!isFieldHiddenLocal('county') && (
            <TextField
              label='County'
              value={formData.county}
              onChange={e => handleChange('county', e.target.value)}
              required={isFieldRequiredLocal('county')}
              sx={{ flex: 1, minWidth: 150 }}
            />
          )}
        </Box>

        {/* Contact Information */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!isFieldHiddenLocal('email') && (
            <TextField
              label='Email'
              type='email'
              value={formData.email}
              onChange={e => handleChange('email', e.target.value)}
              required={isFieldRequiredLocal('email')}
              sx={{ flex: 1, minWidth: 250 }}
              disabled={readOnly}
            />
          )}
          {!isFieldHiddenLocal('telephone') && (
            <TextField
              label='Telephone'
              value={formData.telephone}
              onChange={e => handleChange('telephone', e.target.value)}
              required={isFieldRequiredLocal('telephone')}
              sx={{ flex: 1, minWidth: 250 }}
              disabled={readOnly}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!isFieldHiddenLocal('mobile') && (
            <TextField
              label='Mobile'
              value={formData.mobile}
              onChange={e => handleChange('mobile', e.target.value)}
              required={isFieldRequiredLocal('mobile')}
              sx={{ flex: 1, minWidth: 250 }}
            />
          )}
          {!isFieldHiddenLocal('other_phone') && (
            <TextField
              label='Other Phone'
              value={formData.other_phone}
              onChange={e => handleChange('other_phone', e.target.value)}
              required={isFieldRequiredLocal('other_phone')}
              sx={{ flex: 1, minWidth: 250 }}
            />
          )}
        </Box>

        {/* Physical Address Different */}
        {!isFieldHiddenLocal('physical_address_different') && (
          <TextField
            fullWidth
            label='Physical Address (If Different from Mailing)'
            multiline
            rows={2}
            value={formData.physical_address_different}
            onChange={e => handleChange('physical_address_different', e.target.value)}
            required={isFieldRequiredLocal('physical_address_different')}
          />
        )}

        {/* Emergency Contact */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!isFieldHiddenLocal('emergency_contact_name') && (
            <TextField
              label='Emergency Contact Name'
              value={formData.emergency_contact_name}
              onChange={e => handleChange('emergency_contact_name', e.target.value)}
              required={isFieldRequiredLocal('emergency_contact_name')}
              sx={{ flex: 1, minWidth: 200 }}
            />
          )}
          {!isFieldHiddenLocal('emergency_contact_relationship') && (
            <TextField
              label='Relationship'
              value={formData.emergency_contact_relationship}
              onChange={e => handleChange('emergency_contact_relationship', e.target.value)}
              required={isFieldRequiredLocal('emergency_contact_relationship')}
              sx={{ flex: 1, minWidth: 200 }}
            />
          )}
          {!isFieldHiddenLocal('emergency_contact_phone') && (
            <TextField
              label='Emergency Contact Phone'
              value={formData.emergency_contact_phone}
              onChange={e => handleChange('emergency_contact_phone', e.target.value)}
              required={isFieldRequiredLocal('emergency_contact_phone')}
              sx={{ flex: 1, minWidth: 200 }}
            />
          )}
        </Box>

        {/* Personal Details */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!isFieldHiddenLocal('ssn_last_four') && (
            <TextField
              label='SSN (Last 4 digits)'
              value={formData.ssn_last_four}
              onChange={e => handleChange('ssn_last_four', e.target.value)}
              required={isFieldRequiredLocal('ssn_last_four')}
              inputProps={{ maxLength: 4 }}
              sx={{ flex: 1, minWidth: 150 }}
            />
          )}
          {!isFieldHiddenLocal('birthplace') && (
            <TextField
              label='Birthplace'
              value={formData.birthplace}
              onChange={e => handleChange('birthplace', e.target.value)}
              required={isFieldRequiredLocal('birthplace')}
              sx={{ flex: 1, minWidth: 150 }}
            />
          )}
          {!isFieldHiddenLocal('date_of_birth') && (
            <TextField
              label='Date of Birth'
              type='date'
              value={formData.date_of_birth}
              onChange={e => handleChange('date_of_birth', e.target.value)}
              required={isFieldRequiredLocal('date_of_birth')}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 150 }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!isFieldHiddenLocal('age') && (
            <TextField
              label='Age'
              type='number'
              value={formData.age}
              onChange={e => handleChange('age', e.target.value)}
              required={isFieldRequiredLocal('age')}
              sx={{ flex: 1, minWidth: 100 }}
            />
          )}
          {!isFieldHiddenLocal('marital_status') && (
            <FormControl sx={{ flex: 1, minWidth: 200 }}>
              <InputLabel>Marital Status</InputLabel>
              <Select
                value={formData.marital_status}
                label='Marital Status'
                onChange={e => handleChange('marital_status', e.target.value)}
                disabled={readOnly}
              >
                <MenuItem value='single'>Single</MenuItem>
                <MenuItem value='married'>Married</MenuItem>
                <MenuItem value='divorced'>Divorced</MenuItem>
                <MenuItem value='separated'>Separated</MenuItem>
                <MenuItem value='other'>Other</MenuItem>
                <MenuItem value='widowed'>Widowed</MenuItem>
              </Select>
            </FormControl>
          )}
          {!isFieldHiddenLocal('number_of_children') && (
            <TextField
              label='Number of Children'
              type='number'
              value={formData.number_of_children}
              onChange={e => handleChange('number_of_children', e.target.value)}
              required={isFieldRequiredLocal('number_of_children')}
              sx={{ flex: 1, minWidth: 150 }}
            />
          )}
        </Box>

        {!isFieldHiddenLocal('children_ages') && (
          <TextField
            fullWidth
            label='Children Ages'
            value={formData.children_ages}
            onChange={e => handleChange('children_ages', e.target.value)}
            required={isFieldRequiredLocal('children_ages')}
            placeholder='e.g., 5, 8, 12'
          />
        )}

        {/* License Information */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!isFieldHiddenLocal('drivers_license') && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.drivers_license}
                  onChange={e =>
                    handleChange('drivers_license', e.target.checked)
                  }
                  disabled={readOnly}
                />
              }
              label="Driver's License"
            />
          )}
          {!isFieldHiddenLocal('state_id') && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.state_id}
                  onChange={e => handleChange('state_id', e.target.checked)}
                />
              }
              label='State ID'
            />
          )}
          {!isFieldHiddenLocal('cdl_endorsements') && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.cdl_endorsements}
                  onChange={e =>
                    handleChange('cdl_endorsements', e.target.checked)
                  }
                />
              }
              label='CDL Endorsements'
            />
          )}
        </Box>

        {formData.cdl_endorsements && !isFieldHiddenLocal('cdl_endorsements_type') && (
          <TextField
            fullWidth
            label='CDL Endorsements Type'
            value={formData.cdl_endorsements_type}
            onChange={e => handleChange('cdl_endorsements_type', e.target.value)}
            required={isFieldRequiredLocal('cdl_endorsements_type')}
          />
        )}
      </Box>
    </Box>
  );
};

export default PersonalInformation;
